import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import WebAppContainer from "@/components/WebAppContainer/WebAppContainer";
import Modal from "@/ui/Modal/Modal";
import { FlexColumn } from "@/ui/FlexContainer/FlexContainer";
import Typography from "@/ui/Typography/Typography";
import Button from "@/ui/Button/Button";
import { StarIcon } from "@/assets/icons";
import cn from "classnames";
import Confetti, { ConfettiHandle } from "@/components/Confetti/Confetti";
import { useRampHaptic } from "@/utils/hooks/useRampHaptic";
import { useConfettiShow } from "@/utils/hooks/useConfettiShow";
import BlockPayButton, {
  SubscriptionForm,
} from "@/features/PaymentsModal/PaymentStates";
import { useSubscriptionStore } from "@/store/useSubscriptionStore";
import { usePaymentStatusStore } from "@/store/paymentStatus";
import { useWelcomeInfo } from "@/store/welcomeInfo";
import { useTranslation } from "react-i18next";
import { SubscriptionBlock } from "@/components/WelcomeModal/SrdComponents/SubscriptionBlock";
import Offer from "@/components/Offer/Offer";
import { useServiceType } from "@/utils/hooks/useServiceType";
import cls from "./styles.module.scss";

interface GiftContentProps {
  onClose: () => void;
  title: string;
  text: string;
  btnClass: string;
}

const GiftContent = ({ onClose, title, text, btnClass }: GiftContentProps) => {
  const confettiRef = useRef<ConfettiHandle>(null);

  const { ramp } = useRampHaptic();

  const { fireworkParty, fireFromCorners } = useConfettiShow(confettiRef);

  const onOpenConfetti = () => {
    setTimeout(() => {
      fireFromCorners();
      ramp(25, 15);
    }, 750);
    setTimeout(() => {
      fireworkParty(8, 250);
    }, 1350);
  };

  useEffect(() => {
    onOpenConfetti();
  }, []);

  return (
    <>
      <Confetti ref={confettiRef} />
      <FlexColumn gap={16} alignItems="center" className={cls.text}>
        <div className={cls.present}>
          <StarIcon className={cn(cls.star, cls.starOne)} size={25} />
          <StarIcon className={cn(cls.star, cls.starTwo)} size={43} />
          <StarIcon className={cn(cls.star, cls.starThree)} size={33} />
          <img src="/assets/present.png" alt="present" />
        </div>
        <Typography variant="h3">{title}</Typography>
        <Typography style={{ whiteSpace: "pre-line" }}>{text}</Typography>
        <Button className={btnClass} onClick={onClose} label="Спасибо 🎉" />
      </FlexColumn>
    </>
  );
};

interface NoTwentyOrdersProps {
  showForm: boolean;
  setShowForm: (value: boolean) => void;
}

const NoTwentyOrdersKef = ({ showForm, setShowForm }: NoTwentyOrdersProps) => {
  const { t } = useTranslation();
  const { awardedBonus } = useWelcomeInfo();
  const { showedResult } = usePaymentStatusStore();
  const { isSubscriptionActive } = useSubscriptionStore();

  if (!showedResult && !isSubscriptionActive) setShowForm(true);

  if (showForm)
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <SubscriptionForm backBtn={() => setShowForm(false)} />
      </motion.div>
    );

  return (
    <FlexColumn gap={16}>
      <Typography className={cls.heading}>
        {awardedBonus === "no_20_order_for_3_bonus_day"
          ? t("welcomeModal.threeDaysPassed")
          : t("welcomeModal.advantageousPoints")}
      </Typography>

      <Typography className={cls.notActiveText}>
        {t("welcomeModal.chooseYourOption")}
      </Typography>

      <FlexColumn gap={8} className={cls.infoBlock}>
        <BlockPayButton onClick={() => setShowForm(true)} />
      </FlexColumn>
    </FlexColumn>
  );
};

const NoTwentyOrdersSrd = ({ showForm, setShowForm }: NoTwentyOrdersProps) => {
  const { t } = useTranslation();
  const { awardedBonus } = useWelcomeInfo();
  const { showedResult } = usePaymentStatusStore();
  const { isSubscriptionActive } = useSubscriptionStore();

  if (!showedResult && !isSubscriptionActive) setShowForm(true);

  if (showForm)
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <SubscriptionForm backBtn={() => setShowForm(false)} />
      </motion.div>
    );

  return (
    <FlexColumn gap={16}>
      <Typography className={cls.heading}>
        {awardedBonus === "no_20_order_for_3_bonus_day"
          ? t("welcomeModal.threeDaysPassed")
          : t("welcomeModal.registeredTitle")}
      </Typography>

      <FlexColumn gap={14} className={cls.infoBlock}>
        <SubscriptionBlock onClick={() => setShowForm(true)} />

        <Offer label={t("offer.offerConditions")} />
      </FlexColumn>
    </FlexColumn>
  );
};

export default function RegisteredModal() {
  const { t } = useTranslation();
  const { awardedBonus } = useWelcomeInfo();

  const { isSubscriptionActive } = useSubscriptionStore();
  const { isSravniDohod } = useServiceType();

  const [isOpen, setIsOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const getModalConfig = (status: string | null) => {
    if (!status) return;

    switch (status) {
      case "3_days_subscribe":
        return {
          title: t("welcomeModal.threeDaysPresent"),
          text: t("welcomeModal.hurryFulfillOrders"),
          btnClass: cls.thanksBtnPlusThree,
          wrapperCls: cls.modalPlusThree,
        };

      case "30_days_subscribe":
      case "30_days_subscribe_after_20_orders":
        return {
          title: t("welcomeModal.monthPresent"),
          text: t("welcomeModal.youCompletedOrders"),
          btnClass: cls.thanksBtnPlusMonth,
          wrapperCls: cls.modalPlusMonth,
        };

      default:
        return null;
    }
  };

  const config = getModalConfig(awardedBonus);

  const onOpen = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    onOpen();
  }, []);

  const onClose = () => {
    setIsOpen(false);
  };

  if (!awardedBonus && !isSubscriptionActive)
    return (
      <Modal
        wrapperCls={!showForm ? cls.wrapperNotActiveStatus : undefined}
        contentCls={cls.fullHeight}
        isOpen={isOpen}
        onClose={onClose}
        hideCloseBtn
        ignoreTranslate
        closeOnBackdropClick={!showForm}
      >
        <WebAppContainer>
          {isSravniDohod ? (
            <NoTwentyOrdersSrd showForm={showForm} setShowForm={setShowForm} />
          ) : (
            <NoTwentyOrdersKef showForm={showForm} setShowForm={setShowForm} />
          )}
        </WebAppContainer>
      </Modal>
    );

  if (
    !awardedBonus ||
    (awardedBonus !== "30_days_subscribe" &&
      awardedBonus !== "30_days_subscribe_after_20_orders" &&
      awardedBonus !== "3_days_subscribe" &&
      awardedBonus !== "no_20_order_for_3_bonus_day")
  )
    return;

  return (
    <Modal
      overlayCls={cls.overlay}
      wrapperCls={config?.wrapperCls}
      isOpen={isOpen}
      onClose={onClose}
      hideCloseBtn
      ignoreTranslate
    >
      {config && (
        <GiftContent
          onClose={() => setIsOpen(false)}
          title={config.title}
          text={config.text}
          btnClass={config.btnClass}
        />
      )}
    </Modal>
  );
}
